import { useEffect, useRef, useState } from 'react';

interface UseSwipeToCloseOptions {
  onClose: () => void;
  enabled?: boolean;
  threshold?: number;
}

export const useSwipeToClose = ({ 
  onClose, 
  enabled = true,
  threshold = 100 
}: UseSwipeToCloseOptions) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragY, setDragY] = useState(0);
  const startY = useRef(0);
  const currentY = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Only allow swipe from the top portion of the modal
      const scrollContainer = target.closest('[data-swipe-container]');
      if (!scrollContainer) return;
      
      const scrollTop = (scrollContainer as HTMLElement).scrollTop;
      // Only enable swipe-to-close when at the top of the scroll container
      if (scrollTop > 10) return;

      startY.current = e.touches[0].clientY;
      setIsDragging(true);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      
      currentY.current = e.touches[0].clientY;
      const delta = currentY.current - startY.current;
      
      // Only allow downward swipes
      if (delta > 0) {
        setDragY(delta);
        // Prevent body scroll during swipe
        if (delta > 20) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;
      
      const delta = currentY.current - startY.current;
      
      if (delta > threshold) {
        onClose();
      }
      
      setIsDragging(false);
      setDragY(0);
      startY.current = 0;
      currentY.current = 0;
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, isDragging, onClose, threshold]);

  return {
    isDragging,
    dragY,
    swipeStyles: {
      transform: isDragging ? `translateY(${dragY}px)` : 'translateY(0)',
      transition: isDragging ? 'none' : 'transform 0.2s ease-out',
      opacity: isDragging ? Math.max(0.5, 1 - dragY / 300) : 1,
    }
  };
};
